import { createTheme } from '@mui/material/styles';

const NAVY       = '#0d1d3e';
const NAVY_HOVER = '#162d5a';
const GOLD       = '#c8a96e';

const theme = createTheme({
  direction: 'rtl',

  palette: {
    primary:    { main: NAVY,  contrastText: '#ffffff' },
    secondary:  { main: GOLD,  contrastText: '#ffffff' },
    background: { default: '#faf8f5', paper: '#ffffff' },
    text:       { primary: '#1a1a2e', secondary: '#6b7280' },
  },

  shape: { borderRadius: 12 },

  typography: {
    fontFamily: "'Ploni', Arial, sans-serif",
    h5:    { fontWeight: 700 },
    h6:    { fontWeight: 600 },
    body1: { fontSize: 14 },
    body2: { fontSize: 13 },
  },

  components: {
    // ── Sidebar ─────────────────────────────────────────────────────────────
    RaMenu: {
      styleOverrides: {
        root: { backgroundColor: NAVY, minHeight: '100%', paddingTop: 8 },
      },
    },
    RaSidebar: {
      styleOverrides: {
        root: { backgroundColor: NAVY },
        docked: { height: '100%' },
      },
    },
    MuiListItemButton: {
      styleOverrides: {
        root: {
          color: '#ffffff',
          borderRadius: 8,
          margin: '2px 8px',
          '& .MuiListItemIcon-root': { color: '#ffffff', minWidth: 36 },
          '&.RaMenuItemLink-active': {
            backgroundColor: 'rgba(200,169,110,0.18)',
            '& .MuiListItemIcon-root': { color: GOLD },
          },
          '&:hover': { backgroundColor: 'rgba(200,169,110,0.12)' },
        },
      },
    },

    // ── AppBar ───────────────────────────────────────────────────────────────
    RaAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          color: NAVY,
          boxShadow: '0 1px 4px rgba(0,0,0,0.08)',
          '& .MuiToolbar-root': { direction: 'rtl' },
        },
      },
    },

    // ── SimpleForm / Edit / Create ────────────────────────────────────────────
    RaSimpleForm: {
      styleOverrides: {
        root: { direction: 'rtl' },
      },
    },

    // ── Datagrid ─────────────────────────────────────────────────────────────
    RaDatagrid: {
      styleOverrides: {
        root: { direction: 'rtl' },
      },
    },

    // ── FilterForm ────────────────────────────────────────────────────────────
    RaFilterForm: {
      styleOverrides: {
        root: { direction: 'rtl' },
      },
    },

    // ── Cards & Paper ────────────────────────────────────────────────────────
    MuiCard: {
      styleOverrides: {
        root: { boxShadow: '0 2px 10px rgba(0,0,0,0.07)', borderRadius: 12 },
      },
    },
    MuiPaper: {
      styleOverrides: { root: { borderRadius: 12 } },
    },

    // ── Buttons ──────────────────────────────────────────────────────────────
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 8, textTransform: 'none', fontWeight: 600 },
        containedPrimary: {
          backgroundColor: NAVY,
          '&:hover': { backgroundColor: NAVY_HOVER },
        },
        containedSecondary: {
          // Navy text, not white — white-on-GOLD measures ~2.2:1 contrast,
          // under WCAG AA's 4.5:1 minimum for text. Navy-on-GOLD measures
          // ~7.4:1, matching the proven GOLD-on-navy pattern used elsewhere
          // (e.g. the sidebar's active nav icon) in reverse.
          backgroundColor: GOLD,
          color: NAVY,
          '&:hover': { backgroundColor: '#b8935a' },
        },
      },
    },

    // ── Tables ───────────────────────────────────────────────────────────────
    MuiTableHead: {
      styleOverrides: {
        root: { backgroundColor: NAVY },
      },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          backgroundColor: NAVY,
          color: '#ffffff',
          fontWeight: 700,
          fontSize: 13,
          borderBottom: 'none',
          whiteSpace: 'nowrap',
        },
        body: {
          fontSize: 13,
          borderBottom: '1px solid rgba(0,0,0,0.04)',
        },
      },
    },
    MuiTableRow: {
      styleOverrides: {
        root: {
          '&:hover': { backgroundColor: 'rgba(13,29,62,0.04) !important' },
        },
      },
    },

    // ── Inputs ───────────────────────────────────────────────────────────────
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          backgroundColor: '#ffffff',
          '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: GOLD },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: NAVY },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          '&.Mui-focused': { color: NAVY },
        },
      },
    },

    // ── Chip ─────────────────────────────────────────────────────────────────
    MuiChip: {
      styleOverrides: {
        root: { fontWeight: 600 },
      },
    },

    // ── Divider ──────────────────────────────────────────────────────────────
    MuiDivider: {
      styleOverrides: {
        root: { borderColor: 'rgba(0,0,0,0.07)' },
      },
    },

  },
});

export default theme;
